import { generateIcon, generateName } from "./words";
import type { Identity } from "./types";

export const STORAGE_KEY = "wheretopee.identity.v1";

let inMemory: Identity | null = null;
let inMemoryFallbackActive = false;
let onFallback: ((active: boolean) => void) | null = null;

export function setOnFallback(cb: (active: boolean) => void): void {
  onFallback = cb;
}

function announceFallback(): void {
  if (!inMemoryFallbackActive) {
    inMemoryFallbackActive = true;
    if (onFallback) onFallback(true);
  }
}

function safeGet(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    announceFallback();
    return null;
  }
}

function safeSet(value: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    announceFallback();
  }
}

function newIdentity(): Identity {
  return {
    id: crypto.randomUUID(),
    name: generateName(),
    icon: generateIcon(),
    version: 1,
  };
}

function isValidIdentity(value: unknown): value is Identity {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === "string" &&
    typeof v.name === "string" &&
    typeof v.icon === "string" &&
    v.version === 1
  );
}

export function loadIdentity(): Identity {
  if (inMemory) return inMemory;
  const raw = safeGet();
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (isValidIdentity(parsed)) {
        inMemory = parsed;
        return parsed;
      }
    } catch {
      // fall through to regeneration
    }
  }
  const fresh = newIdentity();
  inMemory = fresh;
  safeSet(JSON.stringify(fresh));
  return fresh;
}

export function saveIdentity(identity: Identity): void {
  inMemory = identity;
  safeSet(JSON.stringify(identity));
}

export function regenerateName(): string {
  return generateName();
}

export function regenerateIcon(): string {
  return generateIcon();
}

export function isInMemoryFallback(): boolean {
  return inMemoryFallbackActive;
}

// Test-only reset.
export function _resetForTests(): void {
  inMemory = null;
  inMemoryFallbackActive = false;
  onFallback = null;
}
